import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { translate } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { Observable, shareReplay, switchMap } from 'rxjs';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { IGridResult, IGridState } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { masketTextBoxValidator } from 'src/app/shared/validators/masketTextBox.validator';
import { StopTimeValidator } from 'src/app/shared/validators/stopTime.validator';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { ITimeEntry } from '../../models/time-entry';
import {
  ISearchWorkTimeViolationsRequest,
  IWorkTimeViolation,
  WorkTimeViolationStatus,
  WorkTimeViolationType
} from '../../models/work-time-violation';
import { TimeEntryAccessService } from '../../services/time-entry-access.service';
import { TimeEntryService } from '../../services/time-entry.service';
import { WorkTimeViolationService } from '../../services/work-time-violation.service';

// Row model for the grid: the TimeSpan strings are shortened to hh:mm once per load.
interface IWorkTimeViolationRow extends IWorkTimeViolation {
  workTimeInWindowShort: string;
  effectiveBreakShort: string;
  missingBreakShort: string;
  windowShort: string;
  entryStartShort: string;
}

@UntilDestroy()
@Component({
    selector: 'app-work-time-violations',
    templateUrl: './work-time-violations.component.html',
    standalone: false
})
export class WorkTimeViolationsComponent implements OnInit {

  constructor(
    private workTimeViolationService: WorkTimeViolationService,
    private timeEntryService: TimeEntryService,
    private accessService: TimeEntryAccessService,
    private store: Store<AppStateInterface>,
    private fb: UntypedFormBuilder,
    private permissionStorage: PermissionStorageService,
    private appNotificationService: AppNotificationService
  ) { }

  WorkTimeViolationType = WorkTimeViolationType;
  WorkTimeViolationStatus = WorkTimeViolationStatus;

  gridData: IGridResult<IWorkTimeViolationRow[]> = {
    data: [],
    total: 0
  };
  gridState: IGridState = { pageSize: 25, skip: 0 };

  // Only the employees whose time data the current user may see — the server refuses the rest.
  employees$: Observable<IEmployee[]> =
    this.timeEntryService.getVisibleEmployees().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  // Default items without a value send no filter; their label is rendered by the template.
  employeeDefaultItem = { id: null, firstName: null, lastName: null };
  typeDefaultItem = { value: null };
  statusDefaultItem = { value: null };
  typeOptions = [
    { value: WorkTimeViolationType.LunchBreak },
    { value: WorkTimeViolationType.UnstoppedTimeEntry }
  ];
  statusOptions = [
    { value: WorkTimeViolationStatus.Open },
    { value: WorkTimeViolationStatus.Corrected },
    { value: WorkTimeViolationStatus.Ignored }
  ];

  // Open violations are what a supervisor has to act on, so they are the default view.
  filterForm = this.fb.group({
    employee: [null],
    dateRangeStart: [],
    dateRangeEnd: [],
    type: [null],
    status: [WorkTimeViolationStatus.Open]
  });

  searchRequest: ISearchWorkTimeViolationsRequest = {
    pageNumber: 1,
    pageSize: 25,
    status: WorkTimeViolationStatus.Open,
    orderBy: ['Date desc']
  };

  // Ignoring a violation is a supervisor decision, guarded like signing a subordinate's day.
  canIgnore = (this.permissionStorage.getPermissions() ?? [])
    .includes('Permissions.DayReportingObject.SignEntry');

  // Correcting edits the time entry, so it follows the time entry write reach: own entries take
  // UpdateSelf, the others the set the server resolved for Update (same rule as the grid).
  private canUpdateSelf = (this.permissionStorage.getPermissions() ?? [])
    .includes('Permissions.TimeEntries.UpdateSelf');
  private updatableIds = new Set<string>();
  private currentEmployeeId: string | null = null;

  get hasActions(): boolean {
    return this.canIgnore || this.canUpdateSelf || this.updatableIds.size > 0;
  }

  violationToIgnore: IWorkTimeViolation | null = null;
  ignoreForm = new UntypedFormGroup({
    remark: new UntypedFormControl(null)
  });

  // The correction dialog: the violation, the entry it edits and the start/stop form. Same
  // masked input and validators as the time entry grid.
  violationToCorrect: IWorkTimeViolationRow | null = null;
  entryToCorrect: ITimeEntry | null = null;
  correctForm: UntypedFormGroup | null = null;
  editMask = 'AB:CD';
  maskRules: { [key: string]: RegExp } = {
    A: /[0-2]+/,
    B: /[0-9]+/,
    C: /[0-5]+/,
    D: /[0-9]+/,
  };

  ngOnInit(): void {
    this.accessService.writableEmployees.Update
      .pipe(untilDestroyed(this))
      .subscribe(employees => this.updatableIds = new Set(employees.map(e => e.id)));

    this.store.select(currentEmployeeSelector)
      .pipe(untilDestroyed(this))
      .subscribe(employee => this.currentEmployeeId = employee?.id ?? null);

    this.updateList();
  }

  updateList() {
    this.workTimeViolationService.searchWorkTimeViolations(this.searchRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.gridData.data = response.data.map(violation => ({
          ...violation,
          workTimeInWindowShort: this.toShort(violation.workTimeInWindow),
          effectiveBreakShort: this.toShort(violation.effectiveBreak),
          missingBreakShort: this.toShort(violation.missingBreak),
          windowShort: `${this.toShort(violation.windowStart)} - ${this.toShort(violation.windowEnd)}`,
          entryStartShort: this.toShort(violation.entryStart)
        }));
        this.gridData.total = response.totalCount;
      });
  }

  onSubmitFilter() {
    const formValues = this.filterForm.value;

    // Dates leave the picker at local midnight; shift them so the backend receives the calendar day.
    this.searchRequest.from = formValues.dateRangeStart
      ? new Date(formValues.dateRangeStart.getTime() - formValues.dateRangeStart.getTimezoneOffset() * 60000)
      : null;
    this.searchRequest.to = formValues.dateRangeEnd
      ? new Date(formValues.dateRangeEnd.getTime() - formValues.dateRangeEnd.getTimezoneOffset() * 60000)
      : null;
    this.searchRequest.employeeIds = formValues.employee?.id ? [formValues.employee.id] : null;
    this.searchRequest.type = formValues.type ?? null;
    this.searchRequest.status = formValues.status ?? null;
    this.resetPaging();

    this.updateList();
  }

  onResetFilter() {
    this.filterForm.reset({ status: WorkTimeViolationStatus.Open });

    this.searchRequest.from = null;
    this.searchRequest.to = null;
    this.searchRequest.employeeIds = null;
    this.searchRequest.type = null;
    this.searchRequest.status = WorkTimeViolationStatus.Open;
    this.resetPaging();

    this.updateList();
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    this.gridState.skip = skip;
    this.gridState.pageSize = take;
    this.searchRequest.pageNumber = Math.round((skip + take) / take);
    this.searchRequest.pageSize = take;

    this.updateList();
  }

  typeName(type: WorkTimeViolationType): string {
    return WorkTimeViolationType[type];
  }

  statusName(status: WorkTimeViolationStatus): string {
    return WorkTimeViolationStatus[status];
  }

  // Reuses the expense status colours: open needs attention, corrected is resolved by the
  // employee, ignored is a supervisor decision.
  statusClass(status: WorkTimeViolationStatus): string {
    switch (status) {
      case WorkTimeViolationStatus.Corrected: return 'status-accepted';
      case WorkTimeViolationStatus.Ignored: return 'status-submitted';
      default: return 'status-rejected';
    }
  }

  // The entry a correction edits: the unstopped entry itself, or the entry the lunch break
  // proposal refers to. Without one there is nothing to correct from here.
  correctableEntryId(violation: IWorkTimeViolation): string | null {
    return violation.type === WorkTimeViolationType.UnstoppedTimeEntry
      ? violation.timeEntryId ?? null
      : violation.suggestedTimeEntryId ?? null;
  }

  canCorrect(violation: IWorkTimeViolation): boolean {
    if (violation.status !== WorkTimeViolationStatus.Open || !this.correctableEntryId(violation)) {
      return false;
    }

    return violation.employeeId === this.currentEmployeeId
      ? this.canUpdateSelf
      : this.updatableIds.has(violation.employeeId);
  }

  onIgnore(violation: IWorkTimeViolation) {
    this.ignoreForm.reset();
    this.violationToIgnore = violation;
  }

  onCancelIgnore() {
    this.violationToIgnore = null;
  }

  onConfirmIgnore() {
    this.workTimeViolationService.ignoreWorkTimeViolation({
      id: this.violationToIgnore.id,
      remark: this.ignoreForm.value.remark?.trim() || null
    })
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.violationToIgnore = null;
        this.appNotificationService.notification$.next({
          message: translate('time-entries.workTimeViolations.ignored'),
          style: 'success'
        });
        this.updateList();
      });
  }

  onCorrect(violation: IWorkTimeViolationRow) {
    this.timeEntryService.getTimeEntry(this.correctableEntryId(violation))
      .pipe(untilDestroyed(this))
      .subscribe(entry => {
        // Lunch break: the proposal is the starting point. Unstopped entry: the stop is what is
        // missing, so it stays empty until typed.
        const isLunchBreak = violation.type === WorkTimeViolationType.LunchBreak;
        const start = isLunchBreak && violation.suggestedStart ? this.toClock(violation.suggestedStart) : this.toShort(entry.start);
        const stop = isLunchBreak && violation.suggestedStop ? this.toClock(violation.suggestedStop) : null;

        this.correctForm = new UntypedFormGroup({
          start: new UntypedFormControl(start, [Validators.required, masketTextBoxValidator('  :  ')]),
          stop: new UntypedFormControl(stop, [Validators.required, masketTextBoxValidator('  :  ')])
        }, { validators: [StopTimeValidator('start', 'stop')], updateOn: 'change' });

        this.entryToCorrect = entry;
        this.violationToCorrect = violation;
      });
  }

  onCancelCorrect() {
    this.violationToCorrect = null;
    this.entryToCorrect = null;
    this.correctForm = null;
  }

  get startIsLargerThanStop(): boolean {
    return !!this.correctForm?.getError('startIsLargerThanStop') && !!this.correctForm?.get('stop')?.touched;
  }

  // Saves through the regular time entry update (same validation, access and recalculation as
  // the grid), then asks for an immediate re-evaluation so the outcome is visible right away.
  onConfirmCorrect() {
    if (!this.correctForm || this.correctForm.invalid) {
      this.correctForm?.markAllAsTouched();
      return;
    }

    const entry = this.entryToCorrect;
    const violation = this.violationToCorrect;

    this.timeEntryService.updateTimeEntry({
      id: entry.id,
      date: new Date(entry.date).toDateString(),
      start: TimeSpan.parse(this.correctForm.value.start).toString(),
      stop: TimeSpan.parse(this.correctForm.value.stop).toString(),
      remarks: entry.remarks,
      employeeId: entry.employeeId,
      timeEntryCategoryId: entry.timeEntryCategoryId ?? entry.timeEntryCategory?.id,
      assignmentId: entry.assignmentId ?? entry.assignment?.id ?? null,
      assignmentServiceId: entry.assignmentServiceId ?? entry.assignmentService?.id ?? null
    } as any)
      .pipe(
        switchMap(() => this.workTimeViolationService.reevaluateWorkTimeViolation(violation.id)),
        untilDestroyed(this)
      )
      .subscribe(result => {
        this.onCancelCorrect();

        const resolved = result.status === WorkTimeViolationStatus.Corrected;
        this.appNotificationService.notification$.next({
          message: translate(resolved
            ? 'time-entries.workTimeViolations.correctedResolved'
            : 'time-entries.workTimeViolations.correctedStillOpen'),
          style: resolved ? 'success' : 'error'
        });
        this.updateList();
      });
  }

  private resetPaging() {
    this.searchRequest.pageNumber = 1;
    this.gridState.skip = 0;
  }

  private toShort(timeSpan: string | null | undefined): string {
    return TimeSpan.parse(timeSpan ?? '00:00:00').toStringShort();
  }

  // "yyyy-MM-ddTHH:mm:ss" (no offset, local wall clock) -> "HH:mm" for the masked input.
  private toClock(dateTime: string): string {
    const date = new Date(dateTime);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
}
